import type { PostHog } from "posthog-js";
import { installPublicChunkReloadListeners, UNRECOVERED_CHUNK_ERROR_PROPERTY, withoutChunkLoadExceptions } from "@/lib/chunk-reload";
/* Only the two canonical keys: astro.config resolves them from the
CI env, the repo-root .env files, then wrangler.jsonc's brand vars.
Aliases used to sit here as a fallback and only hid the fact that
none of them were ever populated. */
const token = import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN;
const host = import.meta.env.VITE_PUBLIC_POSTHOG_HOST || "https://eu.i.posthog.com";
let analyticsClient: Pick<PostHog, "captureException"> | null = null;
/* A failed island or analytics import reloads the page once; Safari's
navigation-cancelled imports are ignored. Exception autocapture
drops these (withoutChunkLoadExceptions below), so the only one that
reaches PostHog is a failure the reload didn't fix. */
installPublicChunkReloadListeners({ onUnrecovered: (error) => {
	analyticsClient?.captureException(error, { [UNRECOVERED_CHUNK_ERROR_PROPERTY]: true });
} });
function scheduleAnalytics() {
	const isLocalhost = /^(localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\])$/.test(window.location.hostname);
	if (!token || isLocalhost || window.self !== window.top) return;
	async function startAnalytics() {
		const [{ default: posthog }, { flushPendingAnalyticsEvents, withNavigationTiming }, { dropKnownNoiseException }] = await Promise.all([
			import("posthog-js"),
			import("@/lib/analytics"),
			import("@/lib/posthog-exception-filter")
		]);
		posthog.init(token, {
			api_host: host,
			autocapture: false,
			capture_exceptions: true,
			before_send: (event) => {
				const kept = withoutChunkLoadExceptions(event);
				return withNavigationTiming(kept && dropKnownNoiseException(kept));
			},
			capture_performance: { web_vitals: true },
			capture_pageleave: true,
			capture_pageview: true,
			defaults: "2026-05-30",
			disable_surveys: true,
			person_profiles: "identified_only",
			ui_host: "https://eu.posthog.com",
			loaded: (client) => {
				analyticsClient = client;
				client.register({ brand: document.documentElement.dataset.brand });
				flushPendingAnalyticsEvents(client);
			}
		});
	}
	void startAnalytics();
}
if (typeof window !== "undefined") scheduleAnalytics();